import React, { useEffect, useState } from 'react';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import DashboardLayout from './components/layout/DashboardLayout';
import { redirectIfInvalid, getAuthStatus, extendSession } from './utils/authService';

// NEW IMPORT - Audio notification manager
import audioNotificationManager from './services/AudioNotificationManager';

// Create a dark theme instance
const darkTheme = createTheme({
  palette: {
    mode: 'dark',
    background: {
      default: '#17212b',
      paper: '#1e2a38',
    },
    primary: {
      main: '#1ABC9C',
    },
    secondary: {
      main: '#4caf50',
    },
  },
  typography: {
    fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          scrollbarWidth: 'thin',
          '&::-webkit-scrollbar': {
            width: '8px',
            height: '8px',
          },
          '&::-webkit-scrollbar-track': {
            background: '#17212b',
          },
          '&::-webkit-scrollbar-thumb': {
            background: '#2a3f55',
            borderRadius: '4px',
          },
        },
      },
    },
  },
});

function App() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  // NEW: Initialize audio notification manager on app start
  useEffect(() => {
    audioNotificationManager.initialize();
    console.log('✅ Audio notification system initialized');
  }, []);

  useEffect(() => {
    // Immediate authentication check - no loading animation
    try {
      const authResult = redirectIfInvalid(); // This will redirect if invalid, or continue if valid
      setIsAuthenticated(true);
    } catch (error) {
      console.error('Authentication failed:', error);
      // redirectIfInvalid will handle the redirect, so this shouldn't execute
    }
  }, []);

  useEffect(() => {
    if (!isAuthenticated) return;

    // Set up session extension on user activity
    const extendSessionOnActivity = () => {
      const authStatus = getAuthStatus();
      if (authStatus.authenticated && authStatus.remainingTime < 60 * 60 * 1000) { // Extend if less than 1 hour remaining
        extendSession();
      }
    };

    // Set up periodic auth check (every 5 minutes)
    const authCheckInterval = setInterval(() => {
      const authStatus = getAuthStatus();
      if (!authStatus.authenticated) {
        console.log('Session expired, redirecting to cclai.in...');
        setIsAuthenticated(false);
        // Clear any stored data before redirect
        sessionStorage.clear();
        window.location.href = 'https://cclai.in';
      }
    }, 5 * 60 * 1000); // 5 minutes

    // Add event listeners for user activity to extend session
    const activities = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart', 'click'];
    let activityTimeout;

    const resetActivityTimer = () => {
      clearTimeout(activityTimeout);
      activityTimeout = setTimeout(extendSessionOnActivity, 30 * 1000); // Check after 30 seconds of activity
    };

    activities.forEach(activity => {
      document.addEventListener(activity, resetActivityTimer, true);
    });

    return () => {
      clearInterval(authCheckInterval);
      clearTimeout(activityTimeout);
      activities.forEach(activity => {
        document.removeEventListener(activity, resetActivityTimer, true);
      });
    };
  }, [isAuthenticated]);

  if (!isAuthenticated) {
    return null;
  }

  return (
    <ThemeProvider theme={darkTheme}>
      <CssBaseline />
      <DashboardLayout />
    </ThemeProvider>
  );
}

export default App;